/* eslint-disable @atlaskit/design-system/ensure-design-token-usage */
/* eslint-disable @atlaskit/design-system/no-html-button */
/** @jsx jsx */
import React, { useEffect, useState } from 'react';
import { cssMap, jsx } from '@compiled/react';
import { useIsHydrateRenderMode } from '@jira/ssr__hydrate-render-mode/src/useIsHydrateRenderMode.tsx';
import { useIsGlobalHydrationComplete } from '@jira/ssr__global-hydration-completeness/src/useIsGlobalHydrationComplete.tsx';
import { token } from '@atlaskit/tokens';

// Added to avoid type errors when checking __HYDRATION_RECOVERABLE_ERRORS__
declare global {
	interface Window {
		__HYDRATION_RECOVERABLE_ERRORS__?: unknown[];
	}
}

const badgeStyles = cssMap({
	badge: {
		backgroundColor: '#8b0000',
		color: '#ffffe0',
		height: '1.5em',
		marginLeft: token('space.100'),
		paddingLeft: token('space.100'),
		paddingRight: token('space.100'),
		borderRadius: token('radius.small'),
		zIndex: 10,
		// The advisory wrapper disables pointer events so it cannot interfere with the page. This
		// badge is interactive, so it opts itself back in.
		pointerEvents: 'all',
		display: 'flex',
		alignItems: 'center',
		gap: token('space.100'),
	},
	button: {
		background: 'none',
		border: 'none',
		color: '#ffffe0',
		cursor: 'pointer',
		font: token('font.body.small'),
		paddingTop: token('space.0'),
		paddingRight: token('space.0'),
		paddingBottom: token('space.0'),
		paddingLeft: token('space.0'),
	},
	toast: {
		backgroundColor: '#8b0000',
		color: '#ffffe0',
		height: '1.5em',
		marginLeft: token('space.100'),
		paddingLeft: token('space.100'),
		paddingRight: token('space.100'),
		borderRadius: token('radius.small'),
		zIndex: 10,
		display: 'flex',
		alignItems: 'center',
	},
});

/**
 * Surfaces recoverable hydration errors to developers, whichever advisory is hosting it.
 *
 * Hydration happens both with and without server side streaming, so this badge is rendered by both
 * HydrationAdvisory and StreamingAdvisory. It is expected to be placed inside an advisory wrapper
 * that owns positioning, and to be rendered only behind the jfp-magma-hydration-advisory gate.
 *
 * See go/jira-debug-ssr-hydration-failures.
 */
export const HydrationErrorBadge = (): React.ReactElement | null => {
	const [hasRecoverableErrors, setHasRecoverableErrors] = useState(false);
	const [isToastVisible, setIsToastVisible] = useState(false);
	const isHydrateRenderMode = useIsHydrateRenderMode();
	const isGlobalHydrationComplete = useIsGlobalHydrationComplete();

	useEffect(() => {
		if (!isGlobalHydrationComplete) {
			return;
		}
		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		if (window.__HYDRATION_RECOVERABLE_ERRORS__?.length) {
			// eslint-disable-next-line react-hooks/set-state-in-effect
			setHasRecoverableErrors(true);
		}
	}, [isGlobalHydrationComplete]);

	useEffect(() => {
		if (!isToastVisible) {
			return;
		}
		const timer = setTimeout(() => {
			setIsToastVisible(false);
		}, 3000);

		return () => {
			clearTimeout(timer);
		};
	}, [isToastVisible]);

	if (!isHydrateRenderMode) {
		return null;
	}

	const handleErrorBadgeClick = () => {
		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		console.log('__HYDRATION_RECOVERABLE_ERRORS__: ', window.__HYDRATION_RECOVERABLE_ERRORS__); // oxlint-disable-line no-console
		setIsToastVisible(true);
	};

	return (
		<>
			{hasRecoverableErrors && (
				<div css={badgeStyles.badge} data-testid="page-container-v2.ui.spa.hydration-error-badge">
					<button type="button" onClick={handleErrorBadgeClick} css={badgeStyles.button}>
						<samp>
							⚠️ Hydration errors detected, click here to reveal in console.
							go/jira-debug-ssr-hydration-failures
						</samp>
					</button>
					<button
						type="button"
						onClick={() => setHasRecoverableErrors(false)}
						css={badgeStyles.button}
					>
						<samp>X</samp>
					</button>
				</div>
			)}
			{isToastVisible && (
				<div css={badgeStyles.toast}>
					<samp>Errors logged to console 🔍</samp>
				</div>
			)}
		</>
	);
};
